import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Product, ProductNameList } from '../../../products/product.model';
import { ProductService } from '../../../products/product.service';
import { finalize, Subscription } from 'rxjs';
import { Company, Factory, ProductionEntryReqBody } from '../../production-inventory.model';

@Component({
  selector: 'app-production-entry-form',
  standalone: false,
  templateUrl: './production-entry-form.component.html',
  styleUrl: './production-entry-form.component.scss',
})
export class ProductionEntryFormComponent implements OnInit, OnDestroy {
  @Input() factoryList: Factory[] = [];
  @Input() companyList: Company[] = [];
  @Output() productionEntryEvent = new EventEmitter<ProductionEntryReqBody>();

  protected productionEntryForm!: FormGroup;
  protected filteredProductList: ProductNameList[] = [];
  protected isProductListLoading = false;
  protected entryPackagingLists: { id: string; label: string }[][] = [[]];
  protected entryPackagingLoading: boolean[] = [false];

  private productValueSubs: Subscription[] = [];
  private productListSub: Subscription | null = null;

  constructor(
    private fb: FormBuilder,
    private productService: ProductService
  ) {}

  ngOnInit(): void {
    this.initForm();
  }

  private initForm(): void {
    this.productionEntryForm = this.fb.group({
      company: [null],
      factory: [null, Validators.required],
      production_date: ['', Validators.required],
      entries: this.fb.array([this.createEntryGroup()]),
    });

    this.productionEntryForm.get('company')!.valueChanges.subscribe(companyId => {
      this.onCompanyChange(companyId);
    });

    this.subscribeToProductChange(0);
  }

  get entries(): FormArray {
    return this.productionEntryForm.get('entries') as FormArray;
  }

  private createEntryGroup(): FormGroup {
    return this.fb.group({
      product: [null, Validators.required],
      product_packaging: [null, Validators.required],
      box_produced: ['', Validators.required],
      batch_number: ['', Validators.required],
      manufacturing_date: [''],
      expiry_date: [''],
      remarks: [''],
    });
  }

  private onCompanyChange(companyId: string | null): void {
    this.entries.controls.forEach((ctrl, i) => {
      ctrl.patchValue({ product: null, product_packaging: null });
      this.entryPackagingLists[i] = [];
    });

    this.productListSub?.unsubscribe();
    this.isProductListLoading = true;
    this.filteredProductList = [];

    this.productListSub = this.productService
      .lighter_list(companyId ?? undefined)
      .pipe(finalize(() => (this.isProductListLoading = false)))
      .subscribe({
        next: products => {
          this.filteredProductList = products;
        },
      });
  }

  private subscribeToProductChange(index: number): void {
    const sub = this.entries
      .at(index)
      .get('product')!
      .valueChanges.subscribe(productId => {
        this.entries.at(index).patchValue({ product_packaging: null });
        this.entryPackagingLists[index] = [];
        if (productId) {
          this.loadPackaging(productId, index);
        }
      });
    this.productValueSubs[index] = sub;
  }

  protected addEntry(): void {
    const newIndex = this.entries.length;
    this.entries.push(this.createEntryGroup());
    this.entryPackagingLists.push([]);
    this.entryPackagingLoading.push(false);
    this.subscribeToProductChange(newIndex);
  }

  protected removeEntry(index: number): void {
    this.entries.removeAt(index);
    this.entryPackagingLists.splice(index, 1);
    this.entryPackagingLoading.splice(index, 1);
    this.productValueSubs[index]?.unsubscribe();
    this.productValueSubs.splice(index, 1);
  }

  private loadPackaging(productId: string, index: number): void {
    this.entryPackagingLoading[index] = true;
    this.productService
      .get(productId)
      .pipe(finalize(() => (this.entryPackagingLoading[index] = false)))
      .subscribe({
        next: ({ product_packaging }: Product) => {
          this.entryPackagingLists[index] = product_packaging.map(p => ({
            id: p.id,
            label: `${p.packing_size} ${p.unit.name}${p.packaging_type ? ' / ' + p.packaging_type.name : ''}`,
          }));
        },
      });
  }

  protected onSubmit(): void {
    if (this.productionEntryForm.invalid) return;
    const v = this.productionEntryForm.value;
    const reqBody: ProductionEntryReqBody = {
      factory: v.factory,
      production_date: v.production_date,
      entries: v.entries.map((e: any) => ({
        product_packaging: e.product_packaging,
        box_produced: e.box_produced,
        batch_number: e.batch_number,
        remarks: e.remarks || undefined,
        manufacturing_date: e.manufacturing_date || undefined,
        expiry_date: e.expiry_date || undefined,
      })),
    };
    this.productionEntryEvent.emit(reqBody);
  }

  ngOnDestroy(): void {
    this.productValueSubs.forEach(s => s?.unsubscribe());
    this.productListSub?.unsubscribe();
  }
}
